import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { faceApi } from '../../../api/faceApi';
import { attendanceApi } from '../../../api/attendanceApi';
import { RootState } from '../../../store/store';

type KioskMode = 'idle' | 'capturing' | 'verifying' | 'punchtype' | 'success' | 'error';

interface VerifiedEmployee {
  id: string;
  name: string;
  code: string;
  confidence: number;
}

const AUTO_CAPTURE_COOLDOWN_MS = 7000;
const FACE_SCAN_INTERVAL_MS = 650;
const STABLE_FACE_DETECTIONS_REQUIRED = 3;
const FACE_SAMPLE_WIDTH = 96;
const FACE_SAMPLE_HEIGHT = 72;

/**
 * Kiosk Page for Anonymous Face-based Attendance
 * Allows employees to check in/out without authentication using face recognition
 * Suitable for shared kiosk devices
 */
export const KioskPage: React.FC = () => {
  const tenantId = useSelector((state: RootState) => state.auth.user?.tenantId);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const faceScanTimeoutRef = useRef<number | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);
  const stableFaceDetectionsRef = useRef(0);
  const lastAutoCaptureAtRef = useRef(0);
  const activeScanIdRef = useRef(0);

  // State
  const [mode, setMode] = useState<KioskMode>('idle');
  const [isCameraActive, setIsCameraActive] = useState(true);
  const [verifiedEmployee, setVerifiedEmployee] = useState<VerifiedEmployee | null>(null);
  const [successfulEmployee, setSuccessfulEmployee] = useState<VerifiedEmployee | null>(null);
  const [lastPunch, setLastPunch] = useState<{ type: string; time: Date } | null>(null);
  const [resultMessage, setResultMessage] = useState('');
  const [manualEmployeeId, setManualEmployeeId] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [idleTimeout, setIdleTimeout] = useState<NodeJS.Timeout | null>(null);
  const [faceScanStatus, setFaceScanStatus] = useState('Enter employee code to start face scan');
  const normalizedEmployeeCode = employeeCode.trim().toUpperCase();
  const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const speak = (message: string) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(message);
    utterance.rate = 0.95;
    window.speechSynthesis.speak(utterance);
  };

  const getApiMessage = (error: any, fallback: string) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback;

  const clearFaceScanTimeout = () => {
    if (faceScanTimeoutRef.current !== null) {
      window.clearTimeout(faceScanTimeoutRef.current);
      faceScanTimeoutRef.current = null;
    }
  };

  const clearResetTimeout = () => {
    if (resetTimeoutRef.current !== null) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  };

  const clearCaptureCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = 1;
    canvas.height = 1;
  };

  const scheduleResetToIdle = (delayMs: number, scanId: number) => {
    clearResetTimeout();
    resetTimeoutRef.current = window.setTimeout(() => {
      if (activeScanIdRef.current === scanId) {
        resetToIdle();
      }
    }, delayMs);
  };

  // Start camera
  const startCamera = async () => {
    try {
      if (streamRef.current && videoRef.current?.srcObject) {
        setIsCameraActive(true);
        return;
      }

      setIsCameraActive(false);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsCameraActive(true);
      }
    } catch (error) {
      setIsCameraActive(false);
      toast.error('Camera not available');
      console.error('Camera error:', error);
    }
  };

  // Stop camera
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    clearCaptureCanvas();
    setIsCameraActive(false);
  };

  // Reset to idle
  const resetToIdle = () => {
    activeScanIdRef.current += 1;
    clearFaceScanTimeout();
    clearResetTimeout();
    setMode('idle');
    setVerifiedEmployee(null);
    setSuccessfulEmployee(null);
    setResultMessage('');
    setManualEmployeeId('');
    setEmployeeCode('');
    setFaceScanStatus('Enter employee code to start face scan');

    // Clear idle timeout
    if (idleTimeout) clearTimeout(idleTimeout);

    // Set new idle timeout (2 minutes)
    const timeout = setTimeout(() => {
      setLastPunch(null);
      resetToIdle();
    }, 120000);

    setIdleTimeout(timeout);
    void startCamera();
  };

  // Verify face
  const verifyFace = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const scanId = activeScanIdRef.current + 1;
    activeScanIdRef.current = scanId;
    clearResetTimeout();
    setVerifiedEmployee(null);
    setSuccessfulEmployee(null);
    clearCaptureCanvas();

    if (!tenantId) {
      const message = 'Tenant context is required for face verification';
      setMode('error');
      setResultMessage(message);
      speak(message);
      toast.error(message);
      return;
    }

    if (!normalizedEmployeeCode) {
      const message = 'Employee code is required for kiosk face verification';
      setMode('error');
      setResultMessage(message);
      speak(message);
      toast.error(message);
      scheduleResetToIdle(3000, scanId);
      return;
    }

    clearFaceScanTimeout();
    stableFaceDetectionsRef.current = 0;
    lastAutoCaptureAtRef.current = Date.now();
    setMode('capturing');

    try {
      // Capture image
      const context = canvasRef.current.getContext('2d');
      if (context && videoRef.current.videoWidth && videoRef.current.videoHeight) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0);
        const faceImageBase64 = canvasRef.current.toDataURL('image/jpeg', 0.6);

        if (activeScanIdRef.current !== scanId) return;
        setMode('verifying');

        // Verify face
        try {
          const result = await faceApi.verifyFace({
            faceImageBase64,
            tenantId,
            employeeCodeHint: normalizedEmployeeCode,
            matchThreshold: 0.78,
          });

          if (activeScanIdRef.current !== scanId) return;

          if (result.isVerified && result.employeeId) {
            const employee = {
              id: result.employeeId,
              name: result.employeeName || 'Unknown',
              code: result.employeeCode || '',
              confidence: result.confidence || 0.9,
            };
            setVerifiedEmployee(employee);
            speak(`Welcome ${employee.name}`);
            await performAutoPunch(employee, faceImageBase64, scanId);
          } else {
            setMode('error');
            const message = result.errorMessage || 'Face not registered';
            setResultMessage(message);
            speak(message);
            toast.warning(message);
            scheduleResetToIdle(3000, scanId);
          }
        } catch (error: any) {
          if (activeScanIdRef.current !== scanId) return;
          setMode('error');
          const message = getApiMessage(error, 'Verification failed');
          setResultMessage(message);
          speak(message);
          toast.error(message);
          scheduleResetToIdle(3000, scanId);
        }
      } else {
        throw new Error('Camera frame is not ready');
      }
    } catch (error) {
      setMode('error');
      setResultMessage('Failed to capture face');
      speak('Failed to capture face');
      toast.error('Failed to capture face');
      scheduleResetToIdle(3000, scanId);
    }
  };

  const hasLikelyFaceInFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return false;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return false;

    canvas.width = FACE_SAMPLE_WIDTH;
    canvas.height = FACE_SAMPLE_HEIGHT;
    context.drawImage(video, 0, 0, FACE_SAMPLE_WIDTH, FACE_SAMPLE_HEIGHT);

    const image = context.getImageData(0, 0, FACE_SAMPLE_WIDTH, FACE_SAMPLE_HEIGHT).data;
    let skinLikePixels = 0;
    let usablePixels = 0;
    let lumaSum = 0;
    let lumaSquaredSum = 0;
    let skinXSum = 0;
    let skinYSum = 0;
    let minSkinX = FACE_SAMPLE_WIDTH;
    let minSkinY = FACE_SAMPLE_HEIGHT;
    let maxSkinX = -1;
    let maxSkinY = -1;

    const startX = Math.floor(FACE_SAMPLE_WIDTH * 0.2);
    const endX = Math.floor(FACE_SAMPLE_WIDTH * 0.8);
    const startY = Math.floor(FACE_SAMPLE_HEIGHT * 0.08);
    const endY = Math.floor(FACE_SAMPLE_HEIGHT * 0.9);

    for (let y = startY; y < endY; y += 1) {
      for (let x = startX; x < endX; x += 1) {
        const offset = (y * FACE_SAMPLE_WIDTH + x) * 4;
        const r = image[offset];
        const g = image[offset + 1];
        const b = image[offset + 2];
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
        const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;

        if (luma > 25 && luma < 245) {
          usablePixels += 1;
          lumaSum += luma;
          lumaSquaredSum += luma * luma;
        }

        if (cb >= 75 && cb <= 145 && cr >= 125 && cr <= 185 && luma > 35) {
          skinLikePixels += 1;
          skinXSum += x;
          skinYSum += y;
          minSkinX = Math.min(minSkinX, x);
          minSkinY = Math.min(minSkinY, y);
          maxSkinX = Math.max(maxSkinX, x);
          maxSkinY = Math.max(maxSkinY, y);
        }
      }
    }

    if (usablePixels === 0 || skinLikePixels === 0) return false;

    const mean = lumaSum / usablePixels;
    const variance = Math.max(0, lumaSquaredSum / usablePixels - mean * mean);
    const standardDeviation = Math.sqrt(variance);
    const skinRatio = skinLikePixels / usablePixels;
    const skinCenterX = skinXSum / skinLikePixels / FACE_SAMPLE_WIDTH;
    const skinCenterY = skinYSum / skinLikePixels / FACE_SAMPLE_HEIGHT;
    const skinBoxWidth = (maxSkinX - minSkinX + 1) / FACE_SAMPLE_WIDTH;
    const skinBoxHeight = (maxSkinY - minSkinY + 1) / FACE_SAMPLE_HEIGHT;
    return (
      skinRatio >= 0.055 &&
      standardDeviation >= 14 &&
      skinCenterX >= 0.24 &&
      skinCenterX <= 0.76 &&
      skinCenterY >= 0.14 &&
      skinCenterY <= 0.84 &&
      skinBoxWidth >= 0.08 &&
      skinBoxHeight >= 0.1
    );
  };

  const scanForFace = async () => {
    clearFaceScanTimeout();

    if (mode !== 'idle' || !isCameraActive) return;

    if (!normalizedEmployeeCode) {
      stableFaceDetectionsRef.current = 0;
      setFaceScanStatus('Enter employee code to start face scan');
      faceScanTimeoutRef.current = window.setTimeout(() => {
        void scanForFace();
      }, FACE_SCAN_INTERVAL_MS);
      return;
    }

    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setFaceScanStatus('Camera is getting ready...');
      faceScanTimeoutRef.current = window.setTimeout(() => {
        void scanForFace();
      }, FACE_SCAN_INTERVAL_MS);
      return;
    }

    const elapsedSinceLastCapture = Date.now() - lastAutoCaptureAtRef.current;
    if (elapsedSinceLastCapture < AUTO_CAPTURE_COOLDOWN_MS) {
      setFaceScanStatus('Please step away for the next punch...');
      faceScanTimeoutRef.current = window.setTimeout(() => {
        void scanForFace();
      }, AUTO_CAPTURE_COOLDOWN_MS - elapsedSinceLastCapture);
      return;
    }

    try {
      if (hasLikelyFaceInFrame()) {
        stableFaceDetectionsRef.current += 1;
        setFaceScanStatus('Face detected. Capturing...');

        if (stableFaceDetectionsRef.current >= STABLE_FACE_DETECTIONS_REQUIRED) {
          void verifyFace();
          return;
        }
      } else {
        stableFaceDetectionsRef.current = 0;
        setFaceScanStatus('Looking for face...');
      }
    } catch (error) {
      stableFaceDetectionsRef.current = 0;
      setFaceScanStatus('Looking for face...');
      console.error('Face scan error:', error);
    }

    faceScanTimeoutRef.current = window.setTimeout(() => {
      void scanForFace();
    }, FACE_SCAN_INTERVAL_MS);
  };

  const performAutoPunch = async (employee: VerifiedEmployee, faceImageBase64: string, scanId: number) => {
    if (activeScanIdRef.current !== scanId) return;
    setMode('verifying');

    try {
      const status = await attendanceApi.getTodayStatus(employee.id);
      if (activeScanIdRef.current !== scanId) return;

      const shouldCheckOut = Boolean(status.isCheckedIn);

      if (shouldCheckOut) {
        await attendanceApi.checkOutAnonymous(
          employee.id,
          'Kiosk - Face Recognition',
          undefined,
          faceImageBase64
        );
        if (activeScanIdRef.current !== scanId) return;

        setLastPunch({ type: 'CHECK_OUT', time: new Date() });
        setVerifiedEmployee(employee);
        setSuccessfulEmployee(employee);
        setResultMessage(`Welcome ${employee.name}. Check-out successful`);
        speak(`Check-out successful`);
        toast.success(`${employee.name} checked out successfully`);
      } else {
        await attendanceApi.checkInAnonymous(
          employee.id,
          'Kiosk - Face Recognition',
          undefined,
          faceImageBase64
        );
        if (activeScanIdRef.current !== scanId) return;

        setLastPunch({ type: 'CHECK_IN', time: new Date() });
        setVerifiedEmployee(employee);
        setSuccessfulEmployee(employee);
        setResultMessage(`Welcome ${employee.name}. Check-in successful`);
        speak(`Check-in successful`);
        toast.success(`${employee.name} checked in successfully`);
      }

      setMode('success');
      scheduleResetToIdle(3500, scanId);
    } catch (error: any) {
      if (activeScanIdRef.current !== scanId) return;
      const message = getApiMessage(error, 'Punch failed');
      const voiceMessage = message;

      setResultMessage(voiceMessage);
      speak(voiceMessage);
      toast.error(voiceMessage);
      setMode('error');
      scheduleResetToIdle(3500, scanId);
    }
  };

  // Perform punch
  const performPunch = async (punchType: 'CHECK_IN' | 'CHECK_OUT') => {
    void punchType;
    if (!verifiedEmployee) return;

    const message = 'Fresh face verification is required';
    setMode('error');
    setVerifiedEmployee(null);
    setResultMessage(message);
    speak(message);
    toast.error(message);
    scheduleResetToIdle(3000, activeScanIdRef.current);
  };

  // Handle manual entry
  const handleManualEntry = async () => {
    if (!manualEmployeeId) {
      toast.warning('Please enter employee ID');
      return;
    }

    setShowManualEntry(false);
    try {
      // Try to identify employee by ID and proceed
      // For now, just reset
      setManualEmployeeId('');
      resetToIdle();
    } catch (error) {
      toast.error('Employee not found');
    }
  };

  // Initialize camera
  useEffect(() => {
    startCamera();

    return () => {
      clearFaceScanTimeout();
      clearResetTimeout();
      stopCamera();
      if (idleTimeout) clearTimeout(idleTimeout);
    };
  }, []);

  useEffect(() => {
    clearFaceScanTimeout();
    stableFaceDetectionsRef.current = 0;

    if (mode === 'idle' && isCameraActive) {
      setFaceScanStatus(normalizedEmployeeCode ? 'Looking for face...' : 'Enter employee code to start face scan');
      faceScanTimeoutRef.current = window.setTimeout(() => {
        void scanForFace();
      }, FACE_SCAN_INTERVAL_MS);
    }

    return clearFaceScanTimeout;
  }, [mode, isCameraActive, normalizedEmployeeCode]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ boxShadow: 4 }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                ATTENDANCE KIOSK
              </Typography>
              <Typography variant="h6" color="textSecondary">
                Face Recognition Check In/Out
              </Typography>
            </Box>

            {mode === 'idle' && (
              <>
                <TextField
                  fullWidth
                  label="Employee Code"
                  placeholder="Enter employee code"
                  value={employeeCode}
                  onChange={(event) => setEmployeeCode(event.target.value.toUpperCase())}
                  disabled={!isCameraActive}
                  sx={{ mb: 3 }}
                  inputProps={{
                    autoComplete: 'off',
                    style: {
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                    },
                  }}
                />

                {/* Camera Feed */}
                <Paper sx={{ bgcolor: '#000', position: 'relative', mb: 3, overflow: 'hidden' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: 'auto',
                      minHeight: 350,
                      display: 'block',
                    }}
                  />
                  {!isCameraActive && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#000',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}

                  {/* Instruction Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 20,
                      left: 0,
                      right: 0,
                      textAlign: 'center',
                      color: 'white',
                    }}
                  >
                    <Typography variant="h6">Position your face</Typography>
                  </Box>
                </Paper>

                {/* Last Punch Info */}
                {lastPunch && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Last punch: {lastPunch.type} at {formatTime(lastPunch.time)}
                  </Alert>
                )}

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  {normalizedEmployeeCode && <CircularProgress size={26} />}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {faceScanStatus}
                  </Typography>
                </Box>
              </>
            )}

            {mode === 'capturing' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6">Capturing face...</Typography>
              </Box>
            )}

            {mode === 'verifying' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6">Verifying identity...</Typography>
              </Box>
            )}

            {mode === 'punchtype' && verifiedEmployee && (
              <Box>
                {/* Employee Info */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
                  <Typography variant="h6" gutterBottom>
                    {verifiedEmployee.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {verifiedEmployee.code}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Confidence: {(verifiedEmployee.confidence * 100).toFixed(0)}%
                  </Typography>
                </Box>

                {/* Punch Options */}
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={() => performPunch('CHECK_IN')}
                    sx={{ py: 1.5, fontSize: '1rem' }}
                  >
                    ✓ CHECK IN
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    size="large"
                    onClick={() => performPunch('CHECK_OUT')}
                    sx={{ py: 1.5, fontSize: '1rem' }}
                  >
                    ✗ CHECK OUT
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={resetToIdle}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            )}

            {mode === 'success' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Success!
                </Typography>
                {successfulEmployee && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {successfulEmployee.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Employee Code: {successfulEmployee.code || '-'}
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" color="textSecondary">
                  {resultMessage || 'Punch recorded successfully'}
                </Typography>
              </Box>
            )}

            {mode === 'error' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Failed
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {resultMessage || 'Please try again'}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'none' }}>
              <canvas ref={canvasRef} />
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Manual Entry Dialog */}
      <Dialog open={showManualEntry} onClose={() => setShowManualEntry(false)}>
        <DialogTitle>Enter Employee ID</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Employee ID or Code"
            value={manualEmployeeId}
            onChange={(e) => setManualEmployeeId(e.target.value)}
            margin="dense"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleManualEntry();
              }
            }}
          />
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={() => setShowManualEntry(false)}>Cancel</Button>
          <Button onClick={handleManualEntry} variant="contained">
            Proceed
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default KioskPage;
